import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import type { MessagePayload, Unsubscribe } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';

import { messagingConfig } from '@/src/config/env';
import { firebaseApp } from '@/src/lib/firebase';
import { functions } from '@/src/lib/functions';
import { UserFacingError } from '@/src/utils/errors';

import {
  currentNotificationPermission,
  isStandalonePwa,
  supportsWebPushApis,
} from './pwa-service';

type RegisterWebPushTokenInput = {
  permission: NotificationPermission;
  standalone: boolean;
  token: string;
  userAgent: string | null;
};

type RevokeWebPushTokenInput = {
  token: string;
};

export type ForegroundPushMessage = {
  body: string;
  title: string;
  url: string;
};

const registerWebPushTokenCallable = httpsCallable<
  RegisterWebPushTokenInput,
  { ok: true }
>(functions, 'registerWebPushToken');
const revokeWebPushTokenCallable = httpsCallable<
  RevokeWebPushTokenInput,
  { ok: true }
>(functions, 'revokeWebPushToken');

async function messagingIsSupported() {
  return supportsWebPushApis() && (await isSupported());
}

async function serviceWorkerRegistration() {
  return navigator.serviceWorker.register('/firebase-messaging-sw.js');
}

export async function canUseWebPush() {
  return messagingIsSupported();
}

type RegisterWebPushTokenOptions = {
  requestPermission?: boolean;
};

export async function registerWebPushToken({
  requestPermission = true,
}: RegisterWebPushTokenOptions = {}) {
  if (!messagingConfig.vapidKey) {
    throw new Error(
      'Firebase web push is not configured yet. Add EXPO_PUBLIC_FIREBASE_VAPID_KEY and rebuild the web app.',
    );
  }

  if (!(await messagingIsSupported())) {
    throw new Error('This browser does not support Frienzzi web push.');
  }

  const permission = requestPermission
    ? await Notification.requestPermission()
    : currentNotificationPermission();

  if (permission !== 'granted') {
    throw new UserFacingError('Browser notification permission was not granted.');
  }

  const registration = await serviceWorkerRegistration();
  const token = await getToken(getMessaging(firebaseApp), {
    serviceWorkerRegistration: registration,
    vapidKey: messagingConfig.vapidKey,
  });

  if (!token) {
    throw new Error('Firebase did not return a push token for this browser.');
  }

  await registerWebPushTokenCallable({
    permission,
    standalone: isStandalonePwa(),
    token,
    userAgent:
      typeof navigator.userAgent === 'string'
        ? navigator.userAgent.slice(0, 500)
        : null,
  });

  return token;
}

export async function refreshGrantedWebPushToken() {
  if (currentNotificationPermission() !== 'granted') {
    return null;
  }

  return registerWebPushToken({ requestPermission: false });
}

export async function revokeWebPushToken(token: string | null) {
  if (!token) {
    return;
  }

  await revokeWebPushTokenCallable({ token });

  if (await messagingIsSupported()) {
    await deleteToken(getMessaging(firebaseApp)).catch(() => undefined);
  }
}

function foregroundPushMessageFromPayload(
  payload: MessagePayload,
): ForegroundPushMessage {
  const data = payload.data ?? {};
  const rawUrl = data.url ?? '/notifications';
  const route = (() => {
    try {
      const parsed = new URL(rawUrl, window.location.origin);

      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return '/notifications';
    }
  })();

  return {
    body:
      data.body ??
      payload.notification?.body ??
      'You have new Frienzzi activity.',
    title: data.title ?? payload.notification?.title ?? 'Frienzzi',
    url: route,
  };
}

export async function subscribeToForegroundPushMessages(
  listener: (message: ForegroundPushMessage) => void,
): Promise<Unsubscribe | null> {
  if (currentNotificationPermission() !== 'granted') {
    return null;
  }

  if (!(await messagingIsSupported())) {
    return null;
  }

  return onMessage(getMessaging(firebaseApp), (payload) => {
    listener(foregroundPushMessageFromPayload(payload));
  });
}
